import React from "react";
import { resolveBackendUrl } from "./accountAvatarUrl";
import defaultProfilePic from "../assets/default_profile_pic.jpeg";
import { getAppDisplayTimezone, getAppTodayIso, parseDbDateTime } from "./appDateTime";

export const getTodayDateString = () => getAppTodayIso();

export const getInitials = (name = "") => {
  const safeName = (name || "").toString();
  const parts = safeName.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0]?.slice(0, 2).toUpperCase() || "NA";
};

export const getAvatarColorClass = (name) => {
  const palette = [
    "bg-red-600",
    "bg-red-500",
    "bg-red-400",
    "bg-green-600",
    "bg-green-500",
    "bg-blue-600",
    "bg-blue-500",
    "bg-yellow-500",
    "bg-purple-500",
    "bg-gray-600",
  ];
  const str = (name || "").toString();
  if (!str) return "bg-slate-600 text-white";
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  return palette[hash % palette.length];
};

export const AvatarSmall = ({ name, src, sizeClass = "w-9 h-9" }) => {
  const resolvedSrc = resolveBackendUrl(src);
  return (
    <div
      className={`${sizeClass} flex-shrink-0 rounded-full flex items-center justify-center overflow-hidden mr-2 border border-gray-200 bg-white`}
      title={name}
    >
      <img
        src={resolvedSrc || defaultProfilePic}
        alt={name || "Staff"}
        className="w-full h-full object-cover"
        onError={(e) => {
          if (e.currentTarget.src !== defaultProfilePic) {
            e.currentTarget.src = defaultProfilePic;
          }
        }}
      />
    </div>
  );
};

export const PlaceholderAvatar = ({ sizeClass = "w-9 h-9" }) => (
  <div className={`${sizeClass} flex-shrink-0 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 mr-2`}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      className="w-5 h-5"
      fill="currentColor"
    >
      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
    </svg>
  </div>
);

export const formatTime = (value) => {
  if (!value) return "";
  try {
    const d = parseDbDateTime(value);
    if (d && !Number.isNaN(d.getTime())) {
      return d
        .toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: getAppDisplayTimezone() })
        .replace(":", ".")
        .toLowerCase();
    }
    const parts = value.match(/(\d{1,2}):(\d{2})/);
    if (parts) {
      const hh = parseInt(parts[1], 10);
      const mm = parts[2];
      const tmp = new Date(Date.UTC(2000, 0, 1, hh, parseInt(mm, 10)));
      return tmp
        .toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: getAppDisplayTimezone() })
        .replace(":", ".")
        .toLowerCase();
    }
    return value;
  } catch {
    return value;
  }
};

export const formatTimeRange = (from, to) => {
  if (!from && !to) return "";
  const f = formatTime(from);
  const t = formatTime(to);
  if (f && t) return `${f} - ${t}`;
  if (f) return f;
  return to || "";
};

export const formatDuration = (val) => {
  if (val === null || val === undefined || val === "") return "";
  if (typeof val === "number") {
    const hours = Math.floor(val);
    const mins = Math.round((val - hours) * 60);
    return mins ? `${hours}h ${mins}m` : `${hours}h`;
  }
  const s = String(val).trim();
  if (/h|m/.test(s) && !/^\d+(?:\.\d+)?$/.test(s)) return s;
  const num = parseFloat(s.replace(/[^0-9.]/g, ""));
  if (!isNaN(num)) {
    const hours = Math.floor(num);
    const mins = Math.round((num - hours) * 60);
    return mins ? `${hours}h ${mins}m` : `${hours}h`;
  }
  return s;
};

export function triggerTimesheetPrint({ orientation = "landscape", isWeekMode = false, documentTitle = "" } = {}) {
  const printContent = document.getElementById("printable-timesheet");
  if (!printContent) return;

  if (window.__timesheetPrintCleanup) {
    window.__timesheetPrintCleanup();
  }

  const originalTitle = document.title;
  let restored = false;
  const restoreTitle = () => {
    if (restored || !documentTitle) return;
    restored = true;
    document.title = originalTitle;
  };

  if (documentTitle) {
    document.title = documentTitle;
  }

  if (window.__timesheetPrintRestoreTimer) {
    window.clearTimeout(window.__timesheetPrintRestoreTimer);
    window.__timesheetPrintRestoreTimer = null;
  }

  // Always force landscape layout for printable timesheets
  printContent.classList.remove("print-day-landscape");
  printContent.classList.add("print-landscape-layout");

  const isTouchPrintClient =
    (typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches) ||
    Number(navigator.maxTouchPoints || 0) > 0 ||
    /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || "");

  // Reset horizontal scroll on mobile containers so print preview always starts at column 1 (left = 0)
  try {
    const scrollContainers = document.querySelectorAll(
      ".timesheet-mobile-scroll, .timesheet-week-view__table-wrap, .timesheet-print-wrap, #printable-timesheet"
    );
    scrollContainers.forEach((c) => {
      if (c && typeof c.scrollLeft === "number") c.scrollLeft = 0;
    });
    if (typeof window.scrollX === "number" && window.scrollX > 0) {
      window.scrollTo(0, window.scrollY);
    }
  } catch {}

  // ─── iOS / Android: open a dedicated popup window containing only the
  //     timesheet HTML + all page stylesheets, then print that popup.
  //     This is the only reliable way to prevent iOS Safari from printing
  //     the entire web page instead of just the timesheet section.
  if (isTouchPrintClient) {
    // Collect all <style> and <link rel="stylesheet"> from the host page
    const styleNodes = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'));
    let stylesHtml = styleNodes
      .map((node) => {
        if (node.tagName === "STYLE") {
          return `<style>${node.textContent}</style>`;
        }
        // <link> — use absolute href so the popup can load it
        const href = node.href || "";
        return href ? `<link rel="stylesheet" href="${href}">` : "";
      })
      .join("\n");

    // Force landscape @page inside the popup
    stylesHtml += `
<style>
  @page { size: landscape; margin: 5mm; }
  html, body { margin: 0; padding: 0; background: #fff; }
  #printable-timesheet { display: block !important; width: 100% !important; }
</style>`;

    const contentHtml = printContent.outerHTML;
    const title = documentTitle || document.title || "Timesheet";

    const popupHtml = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=1024, initial-scale=1">
  <title>${title}</title>
  ${stylesHtml}
</head>
<body>
  ${contentHtml}
  <script>
    window.onload = function() {
      setTimeout(function() {
        window.print();
        // Auto-close after print dialog is dismissed.
        // afterprint fires on most browsers; the setTimeout fallback
        // handles iOS Safari where afterprint is unreliable.
        var closed = false;
        function closePopup() {
          if (closed) return;
          closed = true;
          window.close();
        }
        window.addEventListener('afterprint', function() {
          setTimeout(closePopup, 300);
        });
        // Fallback for iOS Safari: close after a generous delay if still open
        setTimeout(closePopup, 30000);
      }, 400);
    };
  <\/script>
</body>
</html>`;

    // Safari on iOS requires window.open() to be called synchronously in the
    // same event-loop tick as the user gesture (the print button click).
    // We therefore open the popup here (synchronously) and write content into it.
    const popup = window.open("", "_blank", "width=1024,height=768");
    if (popup) {
      popup.document.open();
      popup.document.write(popupHtml);
      popup.document.close();
      // Cleanup: restore state after popup is dismissed
      window.__timesheetPrintCleanup = () => {
        restoreTitle();
        window.__timesheetPrintCleanup = null;
      };
      window.setTimeout(restoreTitle, 10 * 60 * 1000);
    } else {
      // Popup blocked — fall back to main-window print with visibility trick
      _triggerMainWindowPrint({ restoreTitle });
    }
    return;
  }

  // ─── Desktop: use the existing visibility:hidden / visibility:visible approach
  _triggerMainWindowPrint({ restoreTitle });

  if (documentTitle) {
    window.__timesheetPrintRestoreTimer = window.setTimeout(() => {
      restoreTitle();
      window.__timesheetPrintRestoreTimer = null;
    }, 10 * 60 * 1000);
  }
}

/** @private — main-window print used on desktop (and as popup fallback on mobile) */
function _triggerMainWindowPrint({ restoreTitle }) {
  document.body.classList.add("timesheet-printing");

  const metaViewport = document.querySelector("meta[name='viewport']");
  const originalViewport = metaViewport ? metaViewport.getAttribute("content") : "";

  const styleId = "timesheet-print-orientation";
  let el = document.getElementById(styleId);
  if (!el) {
    el = document.createElement("style");
    el.id = styleId;
    document.head.appendChild(el);
  }
  el.textContent = `@media print { @page { size: landscape; margin: 5mm; } html, body, #printable-timesheet, #printable-timesheet * { transform: none !important; } }`;

  let printViewWasHidden = false;
  let printWindowLostFocus = false;

  const cleanupPrintClass = () => {
    document.body.classList.remove("timesheet-printing");
    if (metaViewport && originalViewport) {
      metaViewport.setAttribute("content", originalViewport);
    }
    window.removeEventListener("afterprint", cleanupPrintClass);
    document.removeEventListener("visibilitychange", handleVisChange);
    window.removeEventListener("blur", handleBlur);
    window.removeEventListener("focus", handleFocus);
    if (window.__timesheetPrintCleanup === cleanupPrintClass) {
      window.__timesheetPrintCleanup = null;
    }
    if (typeof restoreTitle === "function") restoreTitle();
  };

  const handleVisChange = () => {
    if (document.visibilityState === "hidden") { printViewWasHidden = true; return; }
    if (printViewWasHidden) window.setTimeout(cleanupPrintClass, 500);
  };
  const handleBlur = () => { printWindowLostFocus = true; };
  const handleFocus = () => { if (printWindowLostFocus) window.setTimeout(cleanupPrintClass, 500); };

  window.addEventListener("afterprint", cleanupPrintClass);
  window.__timesheetPrintCleanup = cleanupPrintClass;
  window.setTimeout(cleanupPrintClass, 60000);

  window.setTimeout(() => { window.print(); }, 100);
}
